import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import authService from "../services/authService";

const OnboardingPage = () => {
  const { user } = useAuth();
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [startYear, setStartYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const professions = [
    { id: "INFORMATIK", label: "Informatik", icon: "fas fa-laptop-code" },
    { id: "MEDIAMATIK", label: "Mediamatik", icon: "fas fa-palette" },
    { id: "KUNDENDIALOG", label: "Kundendialog", icon: "fas fa-comments" },
    { id: "DETAILHANDEL", label: "Detailhandel", icon: "fas fa-shopping-cart" },
    { id: "KV", label: "KV", icon: "fas fa-file-alt" },
  ];

  const handleSubmit = async () => {
    if (!selectedProfession || !startYear) return;

    setLoading(true);
    setError(null);

    try {
      await authService.submitOnboarding({
        professionType: selectedProfession,
        classStartDate: `${startYear}-08-01`
      });

      // Force refresh of user data
      window.location.href = "/";
    } catch (err) {
      console.error("Onboarding error:", err);
      setError("Failed to save profile. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="card-brand-elevated p-6 md:p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-grey-dusk flex items-center mb-2">
                  <i className="fas fa-rocket text-sunrise-red mr-4"></i>
                  Welcome to Journey
                </h1>
                <p className="text-sm md:text-base text-slate-cloud">
                  Select your profession type to complete your profile setup.
                </p>
              </div>

              {/* User context */}
              <div className="bg-white/70 border border-pale-dawn rounded-xl px-4 py-3 text-sm text-grey-dusk">
                <div className="flex items-center gap-2">
                  <i className="fas fa-user text-sunrise-red"></i>
                  <span className="font-semibold">
                    {user?.firstName} {user?.lastName}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {user?.email || "No email available"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto">
          <div className="card-brand overflow-hidden">
            <div className="bg-gradient-to-r from-grey-dusk to-slate-cloud text-white px-6 py-4">
              <h2 className="text-lg font-bold flex items-center">
                <i className="fas fa-briefcase mr-3"></i>
                Profession selection
              </h2>
              <p className="text-sm text-white/90 mt-1">
                This helps us show the correct subjects and workflows.
              </p>
            </div>

            <div className="p-6">
              {/* Error */}
              {error && (
                <div className="mb-5 bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-lg flex items-start">
                  <i className="fas fa-exclamation-triangle mr-3 mt-0.5"></i>
                  <div>
                    <div className="font-semibold">Save failed</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              )}

              {/* Options */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {professions.map((prof) => {
                  const active = selectedProfession === prof.id;

                  return (
                    <button
                      key={prof.id}
                      type="button"
                      onClick={() => setSelectedProfession(prof.id)}
                      className={[
                        "w-full text-left rounded-xl border transition-all duration-200",
                        "px-4 py-4 flex items-center gap-3",
                        active
                          ? "border-sunrise-red bg-sunrise-red/5 ring-2 ring-sunrise-red/30"
                          : "border-pale-dawn bg-white hover:bg-ice-white hover:border-slate-cloud/40",
                      ].join(" ")}
                    >
                      <div
                        className={[
                          "w-11 h-11 rounded-full flex items-center justify-center shrink-0",
                          active ? "bg-sunrise-red/10" : "bg-slate-cloud/10",
                        ].join(" ")}
                      >
                        <i
                          className={[
                            prof.icon,
                            "text-lg",
                            active ? "text-sunrise-red" : "text-slate-cloud",
                          ].join(" ")}
                        ></i>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div
                          className={[
                            "font-semibold",
                            active ? "text-grey-dusk" : "text-grey-dusk",
                          ].join(" ")}
                        >
                          {prof.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Select to continue
                        </div>
                      </div>

                      <div className="shrink-0">
                        {active ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-sunrise-red text-white">
                            <i className="fas fa-check text-sm"></i>
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-400">
                            <i className="fas fa-chevron-right text-sm"></i>
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Class Start Date */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-calendar-alt text-sunrise-red mr-2"></i>
                  Apprenticeship Start Date (Lehrbeginn)
                </label>
                <div className="bg-blue-50 text-blue-800 text-xs p-3 rounded-lg mb-3 flex items-start">
                   <i className="fas fa-info-circle mr-2 mt-0.5"></i>
                   <div>
                     Please enter the start <strong>year</strong> of your apprenticeship (e.g. 2024). 
                     We automatically set the date to <strong>01.08.{startYear || 'YYYY'}</strong>.
                   </div>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="4"
                    required
                    placeholder="YYYY"
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value.replace(/\D/g, ""))}
                    className="w-32 rounded-lg border-gray-300 shadow-sm focus:border-sunrise-red focus:ring-sunrise-red text-sm"
                  />
                  {startYear && startYear.length === 4 && (
                    <span className="text-sm text-gray-500">
                       → 01.08.{startYear}
                    </span>
                  )}
                </div>
              </div>

              {/* Footer actions */}
              <div className="mt-6 pt-5 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                <div className="text-xs text-gray-500 flex items-center">
                  <i className="fas fa-info-circle mr-2 text-sunrise-red"></i>
                  You can change this later in your profile settings.
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!selectedProfession || !startYear || loading}
                  className={[
                    "btn-brand-primary",
                    "px-5 py-2.5 rounded-lg",
                    (!selectedProfession || !startYear || loading) ? "opacity-60 cursor-not-allowed" : "",
                  ].join(" ")}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-circle-notch fa-spin"></i>
                      Saving
                    </>
                  ) : (
                    <>
                      <i className="fas fa-arrow-right"></i>
                      Continue to dashboard
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Optional small helper card */}
          <div className="mt-6 card-brand p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-grey-dusk/10 flex items-center justify-center">
                <i className="fas fa-shield-alt text-grey-dusk"></i>
              </div>
              <div>
                <h3 className="font-semibold text-grey-dusk mb-1">
                  Data and access
                </h3>
                <p className="text-sm text-gray-600">
                  Your profession type is used to personalize subjects, analytics and review templates.
                  It does not change your permissions or roles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightweight overlay during save */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg px-6 py-4 flex items-center gap-3 text-sm text-grey-dusk">
            <i className="fas fa-circle-notch fa-spin text-sunrise-red"></i>
            Saving your selection...
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;
